import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { createRisk, fetchRiskById, updateRisk } from '../../request/risks/risks.api.ts';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import type { CreateRiskInput, RiskRecord, RiskUpsertInput } from '../../request/risks/risks.types.ts';
import { queryKeys } from '../../queryKeys.ts';
import { useAuditYear } from '../../contexts/AuditYearContext.tsx';

export const useRiskById = (riskId: string | undefined): UseQueryResult<RiskRecord | null, Error> => {
  const { selectedAuditYearId } = useAuditYear();
  return useQuery({
    queryKey: queryKeys.risks.detail(riskId || '', selectedAuditYearId),
    queryFn: () => fetchRiskById(riskId as string, selectedAuditYearId),
    enabled: !!riskId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

export const useCreateRisk = (): UseMutationResult<unknown, Error, CreateRiskInput> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, CreateRiskInput>({
    mutationFn: createRisk,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });
      toast.success('Risk created successfully');
    }, 
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to create risk')));
    },
  });
};

export const useUpdateRisk = (): UseMutationResult<
  unknown,
  Error,
  { riskId: string; payload: RiskUpsertInput; auditYearId?: string }
> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, { riskId: string; payload: RiskUpsertInput; auditYearId?: string }>({
    mutationFn: ({ riskId, payload, auditYearId }) => updateRisk(riskId, payload, auditYearId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.risks.detail(variables.riskId) });
      toast.success('Risk updated successfully');
    },
    onError: (error: Error) => {
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Failed to update risk')));
    },
  });
};
